import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";
import { useSnowplowContext } from "@simplybusiness/services";
import { questionnaireCombinedErrorsAtom } from "../state/questionnaireCombinedErrorsAtom";
import { questionnaireAnswersAtom } from "../state/questionnaireAnswersAtom";

const US_STATE_QUESTION_NAMES = ["us_state", "US state"] as const;

export const useQuestionValidationTracking = () => {
  const { snowplow } = useSnowplowContext();
  const errors = useAtomValue(questionnaireCombinedErrorsAtom);
  const answers = useAtomValue(questionnaireAnswersAtom);
  const reportedErrors = useRef<Set<string>>(new Set());
  const reportedUsStateErrors = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!errors || !snowplow) return;

    Object.keys(errors).forEach(questionName => {
      if (!reportedErrors.current.has(questionName)) {
        reportedErrors.current.add(questionName);
        snowplow.trigger("questionValidationFailed", {
          question: questionName,
          errorMessage: errors[questionName],
        });
      }
    });

    reportedErrors.current.forEach(questionName => {
      if (!errors[questionName]) {
        reportedErrors.current.delete(questionName);
      }
    });
  }, [errors, snowplow]);

  // Fire US-state-specific marketing event when state question has an error
  useEffect(() => {
    if (!errors || !snowplow) return;

    US_STATE_QUESTION_NAMES.forEach(questionName => {
      if (
        errors[questionName] &&
        !reportedUsStateErrors.current.has(questionName)
      ) {
        reportedUsStateErrors.current.add(questionName);
        snowplow.trigger("usStateErrorMessageDisplayed", {
          stateValue: (answers?.[questionName] as string) ?? "",
        });
      }
    });

    reportedUsStateErrors.current.forEach(questionName => {
      if (!errors[questionName]) {
        reportedUsStateErrors.current.delete(questionName);
      }
    });
  }, [errors, snowplow, answers]);
};
